import React, { useMemo, useCallback } from 'react';
import i18next, { type Resource } from 'i18next';
import { I18nextProvider } from 'react-i18next';
import type { Node, PageProps } from 'gatsby';
import { FALLBACK_LANGUAGE, resolveLanguage } from './languages';

export interface LocaleNode extends Node {
  ns: string;
  data: Record<string, unknown>;
  language: string;
}

export type GatsbyPageContext = PageProps['pageContext'] & {
  i18n: {
    language: string;
  };
  locales?: LocaleNode[];
};

interface I18nProviderProps {
  children: React.ReactNode;
  pageContext: GatsbyPageContext;
  locales?: LocaleNode[];
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children, pageContext, locales }) => {
  // Build i18n resources from locale nodes
  const buildResources = useCallback((localeNodes?: LocaleNode[]): Resource => {
    const resources: Resource = {};

    if (!localeNodes) {
      return resources;
    }

    for (const node of localeNodes) {
      // Resolve node.language (the BCP 47 locale dir name, e.g. 'ja-JP') to its canonical
      // supported language code, tolerating any legacy bare codes still on disk.
      const { languageCode } = resolveLanguage(node.language);

      // Initialize language resources if needed
      if (!resources[languageCode]) {
        resources[languageCode] = {};
      }

      resources[languageCode][node.ns] = node.data;
    }

    return resources;
  }, []);

  const i18nInstance = useMemo(() => {
    const instance = i18next.createInstance();

    // Process locale nodes
    const resources: Resource = locales ? buildResources(locales) : {};

    instance.init({
      returnObjects: true,
      debug: process.env.NODE_ENV === 'development',
      lng: pageContext.i18n.language,
      fallbackLng: FALLBACK_LANGUAGE,
      resources,
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: false,
      },
    });

    return instance;
  }, [locales, pageContext.i18n.language, buildResources]);

  return <I18nextProvider i18n={i18nInstance}>{children}</I18nextProvider>;
};
